import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import FlattenedPreviewModal from './FlattenedPreviewModal';
import { readSessionRefusal, SessionRefusalShown } from '../utils/sessionSaveGate';
import { readSourcePdfDocTier } from '../utils/readSourcePdfDocTier';
import { useArtifactGate } from './ArtifactGate';
import { useCapabilities } from '../contexts/CapabilitiesContext';
import PDFViewer from './PDFViewer';
import LiveFormPreview from './LiveFormPreview';
import ErrorDialog from './ErrorDialog';
import ProgressIndicator from './ProgressIndicator';
import WorkflowChoice from './WorkflowChoice';
import UploadSection from './UploadSection';
import LoadSessionDialog from './LoadSessionDialog';
import GenerationControls from './GenerationControls';
import GenerateProjectDialog, { ProjectGenerationConfig } from './GenerateProjectDialog';
import LicenseStatus from './LicenseStatus';
import { useSession, sessionActions } from '../contexts/SessionContext';
import { useLicense } from '../contexts/LicenseContext';
import { getApiUrl } from '../config/api-config';
import { getStoredLicenseKeyHash, getStoredUserEmail } from '../services/LicenseService';
import { getFieldValidationErrors } from '../utils/fieldValidation';
import { resolveFieldValue } from '../utils/fieldDataBinding';
import { useAuth } from '../contexts/AuthContext';
import { FieldCoordinates } from '../types/codeGeneration';
import {
  FieldSortPrefs,
  ReadingOrderMode,
  DEFAULT_READING_ORDER_MODE,
  getFieldSortPrefs,
  saveFieldSortPrefs,
  orderForGeneration,
  readingOrderModeFromSession,
} from '../utils/fieldSort';
import { describeDownloadFailure } from '../utils/generationErrors';
import { AccessibilityReport } from '../types/accessibilityReport';

// Constants

/* ---------------------------------------------------------------------------
 * Per-form accessibility properties: document title and language.
 *
 * Both are document-level, both are announced, and both are things only a human
 * can supply. Guidance wording is approved and lives in
 * docs/design/PDFUA_FIELD_PROPERTY_UI_SPEC.md (MICROCOPY items 6 and 7) —
 * written for a municipal clerk, so it says what happens, never what standard
 * requires it.
 * ------------------------------------------------------------------------- */

/** Default document language. Mirrors PdfUaDocumentTier.DEFAULT_LANGUAGE. */
const DEFAULT_DOCUMENT_LANGUAGE = 'en-US';

/**
 * The short list. A free-text BCP-47 box invites typos into the one property
 * that a validator checks mechanically, so the common answers are one click and
 * anything else goes through "Other…". Spanish is second, not buried: several
 * Colorado entities publish in Spanish and this is the control that decides
 * whether their form is read aloud intelligibly.
 */
const DOCUMENT_LANGUAGE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'en-US', label: 'English (United States) — en-US' },
  { value: 'es-US', label: 'Español / Spanish (United States) — es-US' },
  { value: 'es-MX', label: 'Español / Spanish (Mexico) — es-MX' },
  { value: 'en-GB', label: 'English (United Kingdom) — en-GB' },
];

/** MICROCOPY 6 — document title. */
const GUIDANCE_DOCUMENT_TITLE =
  'This is what a screen reader announces when the file opens, and what appears in the window title instead of the filename. “Dog Licence Application” tells someone what they have opened. “form_v3_final.pdf” does not.';

/** MICROCOPY 7 — language. */
const GUIDANCE_DOCUMENT_LANGUAGE =
  'Screen readers use this to choose how to pronounce the words. Set to the wrong language, an English form read by a Spanish voice is close to unusable — and it is one of the few things that makes a file fail automatically.';

/**
 * Filenames a scanner or a camera chose, not a human. Pre-filling the title from
 * one of these produces something that looks authored and says nothing, which is
 * worse than an empty box the clerk has to fill.
 */
const SCAN_ARTEFACT_FILENAME = /^(scan|doc|document|image|img|untitled|new)[\s_-]*\d*$/i;

interface UploadResponse {
  documentId: string;
  originalFilename: string;
  fileSize: number;
  status: string;
  // The backend auto-creates an editing session on every upload; these identify it.
  // Omitted when that auto-creation failed (it fails soft, the upload still succeeds).
  sessionId?: string;
  sessionName?: string;
  // Non-fatal facts about the uploaded file that the author needs to know — today,
  // only that its links will not survive generation
  // (docs/design/WHAT_ACCESSIBLE_STILL_MEANS.md §1.4). Same {message, code} shape as
  // the rejection body a password-protected upload returns, and absent rather than
  // empty when there is nothing to say.
  warnings?: Array<{ code: string; message: string }>;
  // The read-only accessibility scan's report card
  // (docs/design/ACCESSIBILITY_AUDIT_ON_UPLOAD.md). Absent when the scan could not run;
  // the editor then simply shows no card. Flows straight through into the session upload
  // result below, so the card has the same lifetime as the document it describes.
  accessibilityReport?: AccessibilityReport;
  // The source PDF's OWN declared Title and Language, read on upload
  // (docs/design/DOCUMENT_TIER_METADATA_SPEC.md). Each is present only when the uploaded
  // document actually declared it, never blank: sourceTitle beats the filename-derived
  // guess (Gap 1), and sourceLanguage is the same /Lang generation carries forward, so
  // seeding from it stops en-US being sent as the author's choice (Gap 2). Absent → the
  // field keeps its filename / en-US fallback.
  sourceTitle?: string;
  sourceLanguage?: string;
}

const PDFUpload: React.FC = () => {
  // Use centralized session state
  const { state: sessionState, dispatch } = useSession();
  const { state: authState } = useAuth();
  // License info from canonical context (single fetcher, SSE-driven, visibility-refresh)
  const { licenseInfo } = useLicense();
  // Generate project dialog state
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [isGeneratingProject, setIsGeneratingProject] = useState(false);
  const [pendingGenerationFields, setPendingGenerationFields] = useState<any>(null);
  // File and upload states
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Project name state - derived from PDF filename, editable by user
  const [projectName, setProjectName] = useState<string>('');
  // Document title — the form's HUMAN title. Separate from projectName on purpose:
  // projectName is capped at 30 chars because Java class/file names derive from it,
  // and a title truncated to fit a filename limit is exactly the failure a spoken
  // document title exists to fix. Pre-filled from the filename, uncapped.
  const [documentTitle, setDocumentTitle] = useState<string>('');
  // BCP-47 language tag for the whole form. Drives /Lang in the PDF and <html lang>
  // in the generated web form. Defaulted because an absent tag is a guaranteed
  // failure and en-US is right for nearly every form in this market.
  const [documentLanguage, setDocumentLanguage] = useState<string>(DEFAULT_DOCUMENT_LANGUAGE);
  // True once the clerk picks "Other…" — the select cannot distinguish "not in the
  // list yet" from "list entry cleared", so the disclosure needs its own flag.
  const [useCustomLanguage, setUseCustomLanguage] = useState(false);

  // Saved baseline of the two document-tier values. They live in local state, so the
  // reducer's field-snapshot change-detector never sees them — which meant an edit to
  // ONLY the Title or Language left the Save button grey and the switch-session prompt
  // silent, and the change could be lost on a session switch. These hold the last
  // saved/loaded/seeded values; hasUnsavedChanges (below) compares the live values
  // against them, and snapshotDocTier re-baselines wherever the field snapshot resets.
  const [savedDocumentTitle, setSavedDocumentTitle] = useState<string>('');
  const [savedDocumentLanguage, setSavedDocumentLanguage] = useState<string>(DEFAULT_DOCUMENT_LANGUAGE);
  const snapshotDocTier = (title: string, language: string) => {
    setSavedDocumentTitle(title.trim());
    setSavedDocumentLanguage(language.trim());
  };
  // Use centralized selectedFields instead of local state
  const selectedFields = sessionState.selectedFields;
  const [currentView, setCurrentView] = useState<'pdf' | 'form'>('pdf');

  // Tell the Getting Started guide which screen is showing.
  //
  // The guide advances from application STATE - document, fields, saved
  // session - which is right for progress but blind to where the user is
  // actually looking. Opening the Live Form Preview is a move to a different
  // activity, not a step forward, so without this the guide carried on
  // describing the editor. Same event-bus pattern as pfb-project-generated.
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('pfb-view-changed', { detail: { view: currentView } }));
  }, [currentView]);
  // HOW THE AUTHOR BROWSES THE FIELD LIST. Shared by the editor sidebar and the
  // preview sidebar so the two agree, remembered across documents because a view
  // preference is personal, and read by nothing that writes a document.
  const [fieldSortPrefs, setFieldSortPrefs] = useState<FieldSortPrefs>(() => getFieldSortPrefs());
  const handleFieldSortPrefsChange = (prefs: FieldSortPrefs) => {
    setFieldSortPrefs(prefs);
    saveFieldSortPrefs(prefs);
  };

  // WHAT ORDER THE DOCUMENT GETS. A separate fact from the sort above, and held
  // here rather than in localStorage on purpose: a reading order is an editorial
  // decision about ONE layout, so carrying "manual" to the next document would
  // publish that document's raw drawing order as its tab order — D37 itself.
  // It DOES ride the session (editing_sessions.reading_order_mode): a session is
  // one document's saved work, so restoring Manual there restores the decision
  // to exactly the layout it was made about.
  const [readingOrderMode, setReadingOrderMode] = useState<ReadingOrderMode>(DEFAULT_READING_ORDER_MODE);
  // A session load restores the saved mode, and loading a session CHANGES the
  // documentId — which fires the new-document reset below after this render's
  // state updates have landed, clobbering the restore. The ref is the session
  // load telling the reset "this document arrived with a decision"; the reset
  // consumes it once and defaults every other arrival.
  const restoredOrderModeRef = useRef<{ documentId: string; mode: ReadingOrderMode } | null>(null);
  const uploadedDocumentId = sessionState.uploadResult?.documentId;
  useEffect(() => {
    const restored = restoredOrderModeRef.current;
    if (restored && restored.documentId === uploadedDocumentId) {
      // This document arrived via a session load that carried a saved mode.
      setReadingOrderMode(restored.mode);
      restoredOrderModeRef.current = null;
      return;
    }
    // A new document has no reading order the author has decided yet, and the
    // honest default is geometry. A session saved before the mode column
    // existed restores AUTO through the ref branch above — the truth of those
    // sessions, because Manual could not survive a reload when they were saved
    // (readingOrderModeFromSession reads anything but 'MANUAL' as AUTO).
    setReadingOrderMode(DEFAULT_READING_ORDER_MODE);
  }, [uploadedDocumentId]);

  // Workflow path states
  const [workflowPath, setWorkflowPath] = useState<'none' | 'new' | 'continue'>('none');

  // True when THIS component just cleared the session on purpose (selecting a new
  // file / starting a new project). The mirror effect below wipes local state on a
  // session clear so an EXTERNAL reset (EditorResetBridge, license/auth change)
  // can't leave a stale file or workflow step on screen — but those same handlers
  // clear the session and then set the locals they want. Without this flag the
  // effect races them: picking a second file (a prior upload present) tripped the
  // clear, and the effect wiped the just-chosen file and bounced to WorkflowChoice.
  // The ref lets the effect skip a clear we initiated and still honor external ones.
  const localSessionClearRef = useRef(false);

  // The file whose client-side metadata read is the one still wanted. The browser
  // doc-tier read (readSourcePdfDocTier) is async; if the user picks a second file
  // before the first read resolves, its stale result must not seed over the newer
  // file's fields. Each file-select stamps this; a resolved read applies only if it
  // still matches.
  const latestSelectedFileRef = useRef<File | null>(null);

  // True once the author has typed in the Document Title field. The title is
  // auto-seeded twice — the client-side read at file-select and the backend's
  // confirmation on upload — and BOTH must stand down once the author has taken it
  // over, or an edit made before upload is silently reverted to the document's own
  // title. Reset on each new file (a fresh file is a fresh auto-seed).
  const documentTitleTouchedRef = useRef(false);
  // The same for Language. A form with no accessibility at all may declare no /Lang (or a
  // wrong one), and the author may need to set or correct it; once they do, the two
  // auto-seeds (the client-side read and the upload confirmation) must not overwrite their
  // choice. It is then the author's responsibility to ensure the tag is right.
  const documentLanguageTouchedRef = useRef(false);

  // Session management states
  const [showLoadDialog, setShowLoadDialog] = useState(false);
  const [availableSessions, setAvailableSessions] = useState<any[]>([]);
  const [isLoadingSessions, setIsLoadingSessions] = useState(false);
  // Unsaved-changes folds TWO change sources: the reducer's field-snapshot comparison,
  // and the two document-tier values (Title/Language) the reducer cannot see because
  // they live in local state. Reading them here makes the Save button indicator, the
  // switch-session prompt, and the load gate all react to a Title/Language-only edit,
  // which previously slipped through. The baseline is re-snapshot on every save / load /
  // upload-seed, so this reads clean immediately after any of those.
  const docTierDirty =
    documentTitle.trim() !== savedDocumentTitle ||
    documentLanguage.trim() !== savedDocumentLanguage;
  const hasUnsavedChanges = sessionState.hasUnsavedChanges || docTierDirty;
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  // True while currentSessionId is the session the *server* created at upload time,
  // rather than one the user deliberately saved or loaded. It is a real save point —
  // it can be saved into — but the user has never saved anything yet, so the editor
  // must still offer "Save Session", not "Update Session".
  const [isAutoCreatedSession, setIsAutoCreatedSession] = useState(false);
  const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] = useState(false);
  const [pendingSessionId, setPendingSessionId] = useState<string | null>(null);
  const [sessionComparison, setSessionComparison] = useState<any>(null);

  // Error dialog states
  const [errorDialog, setErrorDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    details?: string;
    errorCode?: string;
    timestamp?: string;
  }>({
    isOpen: false,
    title: '',
    message: '',
    details: '',
    errorCode: '',
    timestamp: ''
  });
  
  // Workflow step tracking
  const [currentWorkflowStep, setCurrentWorkflowStep] = useState(0);
  
  const workflowSteps = [
    {
      id: 'start',
      title: 'Start',
      description: 'New or Continue'
    },
    {
      id: 'upload',
      title: 'Upload PDF',
      description: 'Select your form'
    },
    {
      id: 'select',
      title: 'Select Fields',
      description: 'Mark form areas'
    },
    {
      id: 'configure',
      title: 'Configure',
      description: 'Set field properties'
    },
    {
      id: 'generate',
      title: 'Generate',
      description: 'Export your code'
    }
  ];
  
  // Update workflow step based on state
  useEffect(() => {
    if (sessionState.uploadResult.documentId && selectedFields.length > 0) {
      setCurrentWorkflowStep(3); // Configure step
    } else if (sessionState.uploadResult.documentId) {
      setCurrentWorkflowStep(2); // Select Fields step
    } else if (selectedFile && workflowPath === 'new') {
      setCurrentWorkflowStep(1); // Upload step
    } else {
      setCurrentWorkflowStep(0); // Start step
    }
  }, [selectedFile, sessionState.uploadResult.documentId, selectedFields, workflowPath]);

  // Mirror EditorResetBridge's global session clear into component-local state.
  // PDFUpload owns several useState hooks (selectedFile, projectName,
  // workflowPath, currentSessionId, currentView, errorDialog) that the
  // reducer-driven CLEAR_SESSION action can't reach. Without this effect,
  // a license/auth change wipes the global session state but leaves these
  // locals visible — e.g., the file picker still shows the previous PDF
  // filename, the workflow step indicator stays past Start.
  useEffect(() => {
    const sessionCleared =
      !sessionState.uploadResult.documentId && !sessionState.currentSessionId;
    // Consume the intent flag on every run: a clear THIS component initiated has
    // already set its own locals, so skip the wipe. External clears leave the flag
    // false and still reset. (Consuming unconditionally keeps a never-fired flag
    // from going stale — the next populated-session render clears it.)
    const initiatedLocally = localSessionClearRef.current;
    localSessionClearRef.current = false;
    if (sessionCleared && !initiatedLocally) {
      latestSelectedFileRef.current = null;
      documentTitleTouchedRef.current = false;
      documentLanguageTouchedRef.current = false;
      setSelectedFile(null);
      setProjectName('');
      setDocumentTitle('');
      setDocumentLanguage(DEFAULT_DOCUMENT_LANGUAGE);
      setUseCustomLanguage(false);
      // Baseline matches the cleared values, so a reset reads clean, not unsaved.
      snapshotDocTier('', DEFAULT_DOCUMENT_LANGUAGE);
      setWorkflowPath('none');
      setCurrentSessionId(null);
      setIsAutoCreatedSession(false);
      setCurrentView('pdf');
      setErrorDialog({ isOpen: false, title: '', message: '' });
    }
  }, [sessionState.uploadResult.documentId, sessionState.currentSessionId]);

  // Sanitize filename — some browsers (e.g., Opera) append .pdf to files already ending in .pdf
  const sanitizeFilename = (filename: string): string => {
    return filename.replace(/\.pdf\.pdf$/i, '.pdf');
  };

  // Maximum project-name length. Generated Java class/file names derive from this
  // (e.g. <Name>Application.java); capping the source here keeps those filenames
  // within filesystem limits (255-char name / ~260-char path on Windows).
  const MAX_PROJECT_NAME_LENGTH = 30;

  // Utility function to derive project name from PDF filename
  const deriveProjectName = (filename: string): string => {
    // Remove .pdf extension and clean up the name
    const derived = filename
      .replace(/\.pdf$/i, '') // Remove .pdf extension (case insensitive)
      .replace(/[_-]/g, ' ') // Replace underscores and hyphens with spaces
      .replace(/\s+/g, ' ') // Replace multiple spaces with single space
      .trim() // Remove leading/trailing spaces
      .split(' ') // Split into words
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()) // Title case
      .join(' '); // Join back
    // Cap length so downstream class/file names stay valid; trim a trailing partial word.
    return derived.length > MAX_PROJECT_NAME_LENGTH
      ? derived.slice(0, MAX_PROJECT_NAME_LENGTH).replace(/\s+\S*$/, '').trim()
      : derived;
  };

  /**
   * Pre-fill the document title from the filename — the one auto-fill this
   * feature allows.
   *
   * Safe because filenames are HUMAN-authored: a clerk who uploads
   * "Building Permit Application.pdf" already wrote the title. (The same
   * transform applied to a machine-generated field name would be a guess
   * dressed as an answer, which is why field labels are never pre-filled.)
   *
   * Returns '' for a scanner's filename or an all-digit one, so the box stays
   * empty and visibly unfinished rather than confidently wrong.
   */
  const deriveDocumentTitle = (filename: string): string => {
    const stem = filename.replace(/\.pdf$/i, '').replace(/[_-]/g, ' ').replace(/\s+/g, ' ').trim();
    if (!stem || /^\d+$/.test(stem) || SCAN_ARTEFACT_FILENAME.test(stem)) {
      return '';
    }
    return stem
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // File upload handlers
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setError(null);
      // Clear all session state (fields, sessions, document context) before new upload.
      // Flag it as our own clear so the mirror effect doesn't treat it as an external
      // reset and wipe the file we just selected (see localSessionClearRef).
      localSessionClearRef.current = true;
      dispatch(sessionActions.clearSession());
      setCurrentSessionId(null);
      setIsAutoCreatedSession(false);
      // Derive project name from sanitized filename. This is the author-editable name
      // that drives the generated code — it stays filename-derived, never from the PDF.
      const derivedName = deriveProjectName(sanitizeFilename(file.name));
      setProjectName(derivedName);
      // The human document title starts as the same filename guess, uncapped…
      const filenameTitle = deriveDocumentTitle(sanitizeFilename(file.name));
      setDocumentTitle(filenameTitle);
      // A fresh file is a fresh auto-seed: the author has not touched title or language yet.
      documentTitleTouchedRef.current = false;
      documentLanguageTouchedRef.current = false;
      setDocumentLanguage(DEFAULT_DOCUMENT_LANGUAGE);
      setUseCustomLanguage(false);
      // The filename guess and default language are the clean starting point — baseline
      // them so a brand-new selection does not read as unsaved before the author edits.
      snapshotDocTier(filenameTitle, DEFAULT_DOCUMENT_LANGUAGE);

      // …but if the PDF declares its OWN title (and/or language), read it in the browser
      // and seed those fields BEFORE upload, so the author sees the real values straight
      // away instead of the filename guess. The backend re-reads and confirms the same
      // values on upload; this just removes the "filename, then corrects" flicker.
      latestSelectedFileRef.current = file;
      readSourcePdfDocTier(file).then(tier => {
        // Ignore a stale read if the author has since picked another file.
        if (latestSelectedFileRef.current !== file) return;
        if (tier.title && !documentTitleTouchedRef.current) {
          // Seed the document's own title over the filename guess — unless the author
          // has already taken the field over (they may edit within the read window). A
          // seed is an auto-value, not an edit, so re-baseline it: it stays clean.
          setDocumentTitle(tier.title);
          setSavedDocumentTitle(tier.title.trim());
        }
        if (tier.language && !documentLanguageTouchedRef.current) {
          // Match the dropdown case-insensitively (so "EN-US" lands on the en-US entry);
          // a genuinely unlisted tag (e.g. es-ES) goes to the free-text "Other…" box.
          // Skipped once the author has chosen a language themselves.
          const match = DOCUMENT_LANGUAGE_OPTIONS.find(
            opt => opt.value.toLowerCase() === tier.language!.toLowerCase());
          const seeded = match ? match.value : tier.language!;
          setDocumentLanguage(seeded);
          setUseCustomLanguage(!match);
          setSavedDocumentLanguage(seeded.trim());
        }
      });
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select a PDF file');
      return;
    }

    setIsUploading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const uploadHeaders: Record<string, string> = { 'Content-Type': 'multipart/form-data' };
      const uploadHash = getStoredLicenseKeyHash();
      if (uploadHash) {
        uploadHeaders['X-License-Key-Hash'] = uploadHash;
      }
      const response = await axios.post<UploadResponse>(
        getApiUrl('/api/pdf/upload'),
        formData,
        {
          withCredentials: true,
          headers: uploadHeaders,
        }
      );

      // Store upload result in session context (single source of truth)
      dispatch(sessionActions.setUploadResult(response.data));

      // Seed the two document-tier fields from the DOCUMENT itself now that the backend
      // has read the source's own metadata (docs/design/DOCUMENT_TIER_METADATA_SPEC.md).
      // The filename-derived title and the en-US language set at file-select were only a
      // first guess; the document's own declarations beat them.
      //   Gap 1 — a real embedded title beats the filename guess.
      //   Gap 2 — the source /Lang beats en-US, so we stop sending en-US as if the author
      //           chose it and defeating the backend's source-/Lang win. When the source
      //           declares no language we keep en-US as a to-confirm default (the inline
      //           "Choose a language" affordance already prompts for the empty case; a
      //           defaulted en-US reads as settled, so leave a TODO rather than build new UI).
      // Absent values leave the file-select fallbacks untouched.
      const sourceTitle = response.data.sourceTitle;
      if (typeof sourceTitle === 'string' && sourceTitle.trim()) {
        // Baseline the document's own title as the clean post-upload value. We do this
        // even when the author edited before Upload: their edit is kept on screen (the
        // touched guard below) but now reads as UNSAVED against the document's title, so
        // the Save button lights up instead of the edit being silently lost.
        setSavedDocumentTitle(sourceTitle.trim());
        if (!documentTitleTouchedRef.current) {
          // Not edited — adopt the document's title as the shown value too (and it is
          // its own baseline, so the field reads clean).
          setDocumentTitle(sourceTitle);
        }
      }
      const sourceLanguage = response.data.sourceLanguage;
      if (typeof sourceLanguage === 'string' && sourceLanguage.trim()) {
        // BCP-47 tags are case-insensitive, so match the dropdown case-insensitively and show the
        // dropdown's canonical value ("EN-US" -> the "en-US" entry). The backend already
        // canonicalizes casing; this is the matching half, so a standard language never lands in
        // the free-text "Other..." box over a casing difference. A genuinely unlisted language
        // (e.g. a dialect not in the short-list) still goes to free text.
        const match = DOCUMENT_LANGUAGE_OPTIONS.find(
          opt => opt.value.toLowerCase() === sourceLanguage.toLowerCase());
        const seeded = match ? match.value : sourceLanguage;
        // Baseline the source language as the clean post-upload value even when the
        // author chose one before upload: their choice stays on screen (the touched
        // guard) and now reads as unsaved, rather than being reverted to the source.
        setSavedDocumentLanguage(seeded.trim());
        if (!documentLanguageTouchedRef.current) {
          setDocumentLanguage(seeded);
          setUseCustomLanguage(!match);
        }
      }
      // TODO(DOCUMENT_TIER_METADATA_SPEC Gap 2): when the source declared no language we keep
      // en-US as a default to CONFIRM, but present it as if chosen. A dedicated "confirm this
      // default language" affordance (distinct from the empty-value warning) would make that
      // honest; deferred rather than built here.

      // Adopt the session the backend auto-created for this upload
      // (AUTOMATIC_INITIAL_SAVE_POINT.md §3). Without this the editor believes
      // there is nothing to save into, so every "save before you leave" path
      // silently discards the work instead of writing it.
      if (response.data.sessionId) {
        setCurrentSessionId(response.data.sessionId);
        setIsAutoCreatedSession(true);
      } else {
        // Auto-creation failed server-side — fall back to the pre-existing behaviour.
        setCurrentSessionId(null);
        setIsAutoCreatedSession(false);
      }
      // An upload CONSUMES quota - PDFUploadController calls recordPdfUpload -
      // so the session's remaining allowance has just changed. Announce it on
      // the same bus the flattened-PDF and code-generation paths already use,
      // or the status panel keeps showing what it fetched at page load.
      //
      // Visible to a guest in a way it never was to a licensed user: their
      // panel counts DOWN from five, so a stale figure reads as "uploads are
      // not counted" - the opposite of the fair-use bound it is stating.
      window.dispatchEvent(new Event('license-changed'));

      console.log('Upload successful:', response.data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Upload failed');
      console.error('Upload error:', err);
    } finally {
      setIsUploading(false);
    }
  };

  // Session management functions
  const fetchAvailableSessions = async () => {
    try {
      setIsLoadingSessions(true);
      const sessionHeaders: Record<string, string> = {};
      const sessionHash = getStoredLicenseKeyHash();
      if (sessionHash) {
        sessionHeaders['X-License-Key-Hash'] = sessionHash;
      }
      const response = await axios.get(getApiUrl('/api/pdf/sessions'), { withCredentials: true, headers: sessionHeaders });

      // Ensure response.data is an array before sorting
      if (!Array.isArray(response.data)) {
        console.error('Expected array from sessions API, got:', typeof response.data, response.data);
        setAvailableSessions([]);
        return;
      }

      // Sort sessions by updatedAt in descending order (most recent first)
      const sortedSessions = response.data.sort((a: any, b: any) => {
        return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
      });

      setAvailableSessions(sortedSessions);
    } catch (error: any) {
      console.error('Error fetching sessions:', error);
      setErrorDialog({
        isOpen: true,
        title: 'Load Sessions Failed',
        message: 'Failed to load sessions: ' + (error.response?.data?.error || 'Unknown error'),
        details: error.message,
        errorCode: 'SESSION_LOAD_ERROR',
        timestamp: new Date().toISOString()
      });
    } finally {
      setIsLoadingSessions(false);
    }
  };

  const handleLoadSession = async (sessionId: string) => {
    console.log('PDFUpload: handleLoadSession called', { sessionId, hasUnsavedChanges });
    
    // Check for unsaved changes before loading
    if (hasUnsavedChanges) {
      console.log('PDFUpload: Unsaved changes detected, showing confirmation dialog');
      setPendingSessionId(sessionId);
      setShowUnsavedChangesDialog(true);
      return;
    }
    
    console.log('PDFUpload: No unsaved changes, loading session directly');
    // Load the session directly if no unsaved changes
    await loadSessionById(sessionId);
  };

  const loadSessionById = async (sessionId: string, skipComparisonCheck = false) => {
    try {
      const loadHeaders: Record<string, string> = {};
      const loadHash = getStoredLicenseKeyHash();
      if (loadHash) {
        loadHeaders['X-License-Key-Hash'] = loadHash;
      }
      const response = await axios.get(getApiUrl(`/api/pdf/sessions/${sessionId}`), { withCredentials: true, headers: loadHeaders });
      const sessionData = response.data;
      
      // Check for differences between current state and session to load
      if (!skipComparisonCheck && selectedFields.length > 0) {
        console.log('PDFUpload: Checking for session differences', {
          currentFieldsCount: selectedFields.length,
          sessionFieldsCount: sessionData.selectedFields?.length || 0,
          currentFields: selectedFields.map(f => ({ id: f.id, name: f.name })),
          sessionFields: (sessionData.selectedFields || []).map((f: any) => ({ id: f.id, name: f.name }))
        });
        
        const comparison = compareSessionStates(sessionData);
        console.log('PDFUpload: Comparison result:', comparison);
        
        if (comparison.hasDifferences) {
          console.log('PDFUpload: Session differences detected, showing dialog');
          setPendingSessionId(sessionId);
          setSessionComparison(comparison);
          setShowUnsavedChangesDialog(true);
          return;
        } else {
          console.log('PDFUpload: No differences found, loading session directly');
        }
      } else {
        console.log('PDFUpload: Skipping comparison check', { skipComparisonCheck, selectedFieldsLength: selectedFields.length });
      }
      
      // Restore the saved reading-order decision (editing_sessions
      // .reading_order_mode). Set directly for the same-document case — loading
      // another revision of the open document never changes documentId, so the
      // reset effect stays quiet — and recorded in the ref for the
      // changed-document case, where that effect fires after this function's
      // state updates land and would otherwise clobber the restore back to
      // AUTO. A session without the field (saved before the column existed)
      // reads AUTO, honestly: Manual could not survive a reload then.
      const restoredOrderMode = readingOrderModeFromSession(sessionData.readingOrderMode);
      restoredOrderModeRef.current = { documentId: sessionData.documentId, mode: restoredOrderMode };
      setReadingOrderMode(restoredOrderMode);

      // Set the upload result in session context to match the session's document
      dispatch(sessionActions.setUploadResult({
        documentId: sessionData.documentId,
        originalFilename: sessionData.originalFilename,
        fileSize: 0, // Not stored in session
        status: 'success'
      }));
      
      // Set the project name from the session, or derive from filename if not available
      if (sessionData.projectName) {
        setProjectName(sessionData.projectName);
      } else if (sessionData.originalFilename) {
        setProjectName(deriveProjectName(sessionData.originalFilename));
      }

      // DOC-03. Restore the two document-level properties the author supplied;
      // they now ride on the session row (editing_sessions.document_title /
      // .document_language).
      //
      // NOTHING IS RE-DERIVED HERE. This used to call deriveDocumentTitle on the
      // filename and reset the language to en-US, which silently replaced a title
      // the author had typed — the generator then wrote that filename into /Title
      // and dc:title with /DisplayDocTitle vouching for it. A session saved before
      // the columns existed has no stored values, and the right answer for it is an
      // EMPTY box plus the inline warning underneath, not a confident guess. D43.
      setDocumentTitle(sessionData.documentTitle || '');
      const restoredLanguage = sessionData.documentLanguage || '';
      setDocumentLanguage(restoredLanguage);
      // A tag that is not one of the four short-list options came in through
      // "Other…", so put the free-text box back rather than losing the tag to a
      // select that cannot represent it.
      setUseCustomLanguage(
        restoredLanguage !== '' && !DOCUMENT_LANGUAGE_OPTIONS.some(opt => opt.value === restoredLanguage)
      );
      // The loaded values ARE the saved baseline — a freshly loaded session reads clean
      // until the author edits the Title or Language.
      snapshotDocTier(sessionData.documentTitle || '', restoredLanguage);

      // Set the selected fields from the session
      dispatch(sessionActions.setSelectedFields(sessionData.selectedFields || []));

      // Set workflow path to continue
      setWorkflowPath('continue');

      // Update current session tracking in both local and context state
      setCurrentSessionId(sessionId);
      setIsAutoCreatedSession(false);
      dispatch(sessionActions.setCurrentSession(
        sessionId,
        sessionData.sessionName,
        sessionData.deploymentStatus
      ));

      console.log('PDFUpload: Setting hasUnsavedChanges = false due to session load');
      dispatch(sessionActions.setUnsavedChanges(false));
      
      setShowLoadDialog(false);
      console.log('Session loaded successfully:', sessionData);
    } catch (error: any) {
      console.error('Error loading session:', error);
      setErrorDialog({
        isOpen: true,
        title: 'Load Session Failed',
        message: 'Failed to load session: ' + (error.response?.data?.error || 'Unknown error'),
        details: error.message,
        errorCode: 'SESSION_LOAD_ERROR',
        timestamp: new Date().toISOString()
      });
    }
  };

  // Workflow handlers
  const handleSelectWorkflow = (workflow: 'new' | 'continue') => {
    if (workflow === 'new') {
      // Clear all session state when starting a new project. Flag it as our own
      // clear so the mirror effect doesn't bounce us straight back to WorkflowChoice
      // when a prior upload was present (see localSessionClearRef).
      localSessionClearRef.current = true;
      dispatch(sessionActions.clearSession());
      setCurrentSessionId(null);
      // The mirror effect skips its reset for our own clears (localSessionClearRef), so
      // clear the document-tier values and their baseline here — otherwise a dirty
      // Title/Language from the previous form would make the new project read unsaved.
      setDocumentTitle('');
      setDocumentLanguage(DEFAULT_DOCUMENT_LANGUAGE);
      setUseCustomLanguage(false);
      documentTitleTouchedRef.current = false;
      documentLanguageTouchedRef.current = false;
      snapshotDocTier('', DEFAULT_DOCUMENT_LANGUAGE);
    }
    setWorkflowPath(workflow);
    if (workflow === 'continue') {
      setShowLoadDialog(true);
      fetchAvailableSessions();
    }
  };

  const handleBackToWorkflowChoice = () => {
    setWorkflowPath('none');
    latestSelectedFileRef.current = null;
    documentTitleTouchedRef.current = false;
    documentLanguageTouchedRef.current = false;
    setSelectedFile(null);
    // Clear all session state (fields, sessions, document context)
    dispatch(sessionActions.clearSession());
    setCurrentSessionId(null);
    setError(null);
  };

  const handleCloseLoadDialog = () => {
    setShowLoadDialog(false);
    if (workflowPath === 'continue') {
      setWorkflowPath('none'); // Go back to workflow choice if they cancel
    }
  };

  // Handle field selection from PDF viewer
  const handleFieldSelect = (coordinates: FieldCoordinates) => {
    console.log('PDFUpload: Field selected from PDF viewer:', coordinates);

    const existingIndex = selectedFields.findIndex(field => field.id === coordinates.id);

    if (existingIndex >= 0) {
      // Update existing field - reducer will compare with snapshot and set hasUnsavedChanges
      dispatch(sessionActions.updateField(coordinates));
    } else {
      // Add new field - reducer will compare with snapshot and set hasUnsavedChanges
      dispatch(sessionActions.addField(coordinates));
    }
  };

  // Live Form Preview values are PREVIEW-ONLY. They must not be written into the
  // session's fields: doing so rewrote each field's Default Value on every
  // keystroke, dirtied the session ("Unsaved Changes" gate on Generate, QA
  // 2026-08-16), and would have persisted sample data as real defaults on the
  // next Update Session. The preview keeps its own formData; the PDF generation
  // handlers below overlay that formData onto defaultValue when they build the
  // FormConfiguration payload, which is all the flatten/fill paths ever needed.
  const handleFormDataChange = (_fieldId: string, _value: any) => {
    // intentionally no session mutation
  };

  // Handle field update from field editor
  const handleFieldUpdate = (updatedField: FieldCoordinates) => {
    console.log('PDFUpload: Field update received:', updatedField);
    // Use dispatch to update field - reducer will compare with snapshot and set hasUnsavedChanges
    dispatch(sessionActions.updateField(updatedField));
  };

  // Handle field deletion from field editor
  /**
   * The author has rearranged the fields (D37). The array IS the order - there
   * is no ordinal to renumber. Rearranging does not by itself decide what the
   * document gets: readingOrderMode does that, and the sidebar sets it to MANUAL
   * in the same act so the rearrangement cannot be a change with no effect.
   */
  const handleFieldsReorder = (reordered: FieldCoordinates[]) => {
    dispatch(sessionActions.reorderFields(reordered));
  };

  const handleFieldDelete = (fieldId: string) => {
    // Use dispatch to delete field - reducer will compare with snapshot and set hasUnsavedChanges
    dispatch(sessionActions.deleteField(fieldId));
  };

  // Unsaved changes dialog handlers
  const handleDiscardChanges = async () => {
    setShowUnsavedChangesDialog(false);
    console.log('PDFUpload: Setting hasUnsavedChanges = false due to discard changes');
    dispatch(sessionActions.setUnsavedChanges(false));
    if (pendingSessionId) {
      await loadSessionById(pendingSessionId, true); // Skip comparison check since user confirmed
      setPendingSessionId(null);
      setSessionComparison(null);
    } else {
      // No session to load into — discarding means reverting to the last saved baseline,
      // including the two document-tier values (the reducer flag cleared above does not
      // reach them). Otherwise a Title/Language edit would survive a "discard" and keep
      // the derived unsaved flag stuck on.
      setDocumentTitle(savedDocumentTitle);
      setDocumentLanguage(savedDocumentLanguage);
      setUseCustomLanguage(
        savedDocumentLanguage !== '' &&
        !DOCUMENT_LANGUAGE_OPTIONS.some(opt => opt.value === savedDocumentLanguage)
      );
      documentTitleTouchedRef.current = false;
      documentLanguageTouchedRef.current = false;
    }
  };

  // Function to compare current session state with another session
  const compareSessionStates = (sessionToLoad: any) => {
    const currentFields = selectedFields;
    const sessionFields = sessionToLoad.selectedFields || [];
    
    // Always do detailed comparison, regardless of field counts
    const differences = [];
    
    // Add field count difference if present
    if (currentFields.length !== sessionFields.length) {
      differences.push(`Field count: ${currentFields.length} → ${sessionFields.length}`);
    }
    
    // Match fields by NAME, not id. A freshly-drawn field carries a client-generated id,
    // but a saved-then-reloaded field has its id set to the field NAME
    // (SessionService.convertFieldConfigurationToFieldCoordinates: "field name as unique ID").
    // Keying on id therefore reported every field of a just-saved session as removed-and-re-added
    // on the immediate reload. field_name is unique per document (the (document_id, field_name)
    // constraint), so name is the stable cross-save identity.
    const currentFieldsMap = new Map(currentFields.map((f: any) => [f.name, f]));
    const sessionFieldsMap = new Map(sessionFields.map((f: any) => [f.name, f]));

    // Check for fields in current but not in session to load
    for (const field of currentFields) {
      if (!sessionFieldsMap.has(field.name)) {
        differences.push(`➖ Field "${field.name}" (${field.type}) will be removed`);
      }
    }

    // Check for fields in session to load but not in current
    for (const field of sessionFields) {
      if (!currentFieldsMap.has(field.name)) {
        differences.push(`➕ Field "${field.name}" (${field.type}) will be added`);
      }
    }

    // Check for differences in existing fields
    for (const field of currentFields) {
      const sessionField = sessionFieldsMap.get(field.name) as any;
      if (sessionField) {
        const fieldDifferences = [];
        
        if (field.name !== sessionField.name) {
          fieldDifferences.push(`name: "${field.name}" → "${sessionField.name}"`);
        }
        if (field.type !== sessionField.type) {
          fieldDifferences.push(`type: "${field.type}" → "${sessionField.type}"`);
        }
        // Null-normalised: '', null and undefined all mean "no value", so a lossless
        // save→reload round-trip (which may return null where the editor held '') does not
        // read as a modification.
        if (Boolean(field.required) !== Boolean(sessionField.required)) {
          fieldDifferences.push(`required: ${Boolean(field.required)} → ${Boolean(sessionField.required)}`);
        }
        if ((field.formatHint || '') !== (sessionField.formatHint || '')) {
          fieldDifferences.push(`formatHint: "${field.formatHint || ''}" → "${sessionField.formatHint || ''}"`);
        }
        if ((field.defaultValue || '') !== (sessionField.defaultValue || '')) {
          fieldDifferences.push(`defaultValue: "${field.defaultValue || ''}" → "${sessionField.defaultValue || ''}"`);
        }
        
        // Compare position changes
        if (field.x !== sessionField.x || field.y !== sessionField.y) {
          fieldDifferences.push(`position: (${Math.round(field.x)}, ${Math.round(field.y)}) → (${Math.round(sessionField.x)}, ${Math.round(sessionField.y)})`);
        }
        
        // Compare size changes
        if (field.width !== sessionField.width || field.height !== sessionField.height) {
          fieldDifferences.push(`size: ${Math.round(field.width)}×${Math.round(field.height)} → ${Math.round(sessionField.width)}×${Math.round(sessionField.height)}`);
        }
        
        // Compare page changes
        if (field.pageNumber !== sessionField.pageNumber) {
          fieldDifferences.push(`page: ${field.pageNumber} → ${sessionField.pageNumber}`);
        }
        
        // Compare character block config if present
        if (field.characterBlockConfig || sessionField.characterBlockConfig) {
          const currentConfig = field.characterBlockConfig as any;
          const sessionConfig = sessionField.characterBlockConfig as any;
          
          if (!currentConfig && sessionConfig) {
            fieldDifferences.push('character block config: none → configured');
          } else if (currentConfig && !sessionConfig) {
            fieldDifferences.push('character block config: configured → none');
          } else if (currentConfig && sessionConfig) {
            if (currentConfig.totalBlocks !== sessionConfig.totalBlocks) {
              fieldDifferences.push(`character blocks: ${currentConfig.totalBlocks} → ${sessionConfig.totalBlocks}`);
            }
            if (currentConfig.blockSpacing !== sessionConfig.blockSpacing) {
              fieldDifferences.push(`block spacing: ${currentConfig.blockSpacing} → ${sessionConfig.blockSpacing}`);
            }
            if (currentConfig.blockWidth !== sessionConfig.blockWidth) {
              fieldDifferences.push(`block width: ${currentConfig.blockWidth || 'default'} → ${sessionConfig.blockWidth || 'default'}`);
            }
          }
        }
        
        // Compare radio group config if present
        if (field.radioGroupConfig || sessionField.radioGroupConfig) {
          const currentRadio = field.radioGroupConfig;
          const sessionRadio = sessionField.radioGroupConfig;
          
          if (!currentRadio && sessionRadio) {
            fieldDifferences.push('radio group config: none → configured');
          } else if (currentRadio && !sessionRadio) {
            fieldDifferences.push('radio group config: configured → none');
          } else if (currentRadio && sessionRadio) {
            if (currentRadio.groupName !== sessionRadio.groupName) {
              fieldDifferences.push(`radio group name: "${currentRadio.groupName}" → "${sessionRadio.groupName}"`);
            }
            if (currentRadio.options?.length !== sessionRadio.options?.length) {
              fieldDifferences.push(`radio options count: ${currentRadio.options?.length || 0} → ${sessionRadio.options?.length || 0}`);
            }
          }
        }
        
        // Compare select config if present
        if (field.selectConfig || sessionField.selectConfig) {
          const currentSelect = field.selectConfig;
          const sessionSelect = sessionField.selectConfig;
          
          if (!currentSelect && sessionSelect) {
            fieldDifferences.push('select config: none → configured');
          } else if (currentSelect && !sessionSelect) {
            fieldDifferences.push('select config: configured → none');
          } else if (currentSelect && sessionSelect) {
            if (currentSelect.options?.length !== sessionSelect.options?.length) {
              fieldDifferences.push(`select options count: ${currentSelect.options?.length || 0} → ${sessionSelect.options?.length || 0}`);
            }
            if (currentSelect.allowMultiple !== sessionSelect.allowMultiple) {
              fieldDifferences.push(`allow multiple: ${currentSelect.allowMultiple} → ${sessionSelect.allowMultiple}`);
            }
          }
        }
        
        if (fieldDifferences.length > 0) {
          differences.push(`🔄 Field "${field.name}": ${fieldDifferences.join(', ')}`);
        }
      }
    }
    
    // Categorize differences for better summary
    const addedFields = differences.filter(d => d.startsWith('➕')).length;
    const removedFields = differences.filter(d => d.startsWith('➖')).length;
    const modifiedFields = differences.filter(d => d.startsWith('🔄')).length;
    const countDifference = differences.filter(d => d.startsWith('Field count:')).length > 0;
    
    let summary = '';
    if (addedFields || removedFields || modifiedFields) {
      const parts = [];
      if (addedFields) parts.push(`${addedFields} added`);
      if (removedFields) parts.push(`${removedFields} removed`);
      if (modifiedFields) parts.push(`${modifiedFields} modified`);
      summary = `Fields: ${parts.join(', ')}`;
    } else if (countDifference) {
      summary = 'Field count difference detected';
    } else {
      summary = 'No differences detected';
    }
    
    return {
      hasDifferences: differences.length > 0,
      summary,
      details: differences,
      counts: { added: addedFields, removed: removedFields, modified: modifiedFields }
    };
  };

  const saveCurrentSession = async () => {
    if (!currentSessionId || !sessionState.uploadResult.documentId) {
      throw new Error('No current session to save');
    }

    // sessionName is required by POST /api/pdf/sessions/save. Prefer whatever the
    // session is already called (a loaded session, or the name the server gave the
    // auto-created one), then the project name, then the file it came from.
    const resolvedSessionName =
      sessionState.sessionName ||
      sessionState.uploadResult.sessionName ||
      projectName ||
      sessionState.uploadResult.originalFilename;

    const sessionData = {
      sessionId: currentSessionId,
      sessionName: resolvedSessionName,
      documentId: sessionState.uploadResult.documentId,
      originalFilename: sessionState.uploadResult.originalFilename,
      projectName: projectName,
      selectedFields,
      // DOC-03. The two authored document-level properties travel with every save,
      // including this auto-save — otherwise "save before loading another session"
      // would be the one path that still discards them.
      documentTitle: documentTitle.trim(),
      documentLanguage: documentLanguage.trim(),
      // The reading-order decision travels too (0d). THE MODE, never the view:
      // fieldSortPrefs must not appear anywhere in this payload, for the 981b8e3
      // reason — a browsing sort is not an authoring decision.
      readingOrderMode: readingOrderMode,
      deploymentStatus: 'development', // Default status for auto-saves
      changeComment: 'Saved pending field changes before loading another session',
      createdByUserEmail: authState.user?.email || authState.user?.displayName || null // Signed-in user (email; displayName holds it when email is null)
    };

    console.log('Auto-saving current session:', sessionData);

    const saveHeaders: Record<string, string> = { 'Content-Type': 'application/json' };
    const saveHash = getStoredLicenseKeyHash();
    if (saveHash) {
      saveHeaders['X-License-Key-Hash'] = saveHash;
    }
    const saveEmail = getStoredUserEmail();
    if (saveEmail) {
      saveHeaders['X-User-Email'] = saveEmail;
    }
    const response = await fetch(getApiUrl('/api/pdf/sessions/save'), {
      method: 'POST',
      credentials: 'include',
      headers: saveHeaders,
      body: JSON.stringify(sessionData),
    });

    if (!response.ok) {
      // A capacity refusal is not an error. It has its own prompt with a way
      // forward, so it must not also surface as a red failure notification.
      const refusal = readSessionRefusal(response.status, response.headers);
      if (refusal) {
        if (refusal.kind === 'limit') {
          showLimit(refusal.message);
        } else {
          showGate('sessions');
        }
        throw new SessionRefusalShown(refusal.message);
      }

      const errorText = await response.text();
      let errorMessage = `Failed to save session: ${response.status}`;
      try {
        const errorJson = JSON.parse(errorText);
        errorMessage = errorJson.error || errorMessage;
      } catch (e) {
        errorMessage += ` - ${errorText}`;
      }
      throw new Error(errorMessage);
    }

    const savedSession = await response.json();
    console.log('Session auto-saved successfully:', savedSession);
    return savedSession;
  };

  const handleSaveAndContinue = async () => {
    // Save current session if we have one
    if (currentSessionId) {
      try {
        await saveCurrentSession();
        setShowUnsavedChangesDialog(false);
        if (pendingSessionId) {
          await loadSessionById(pendingSessionId, true); // Skip comparison check since user confirmed
          setPendingSessionId(null);
          setSessionComparison(null);
        }
      } catch (error) {
        // The refusal prompt is already open; a second message would fight it.
        if (error instanceof SessionRefusalShown) {
          return;
        }
        console.error('Error saving session:', error);
        setError('Failed to save changes. Please try again.');
      }
    } else {
      // No current session to save, just discard changes
      await handleDiscardChanges();
    }
  };

  const handleCancelLoadSession = () => {
    setShowUnsavedChangesDialog(false);
    setPendingSessionId(null);
    setSessionComparison(null);
  };

  // Error dialog handlers
  const handleErrorDialogClose = () => {
    setErrorDialog(prev => ({ ...prev, isOpen: false }));
  };

  const handleErrorDialogRetry = () => {
    setErrorDialog(prev => ({ ...prev, isOpen: false }));
  };

  // Download the original, untouched uploaded PDF — the server already
  // retains it unmodified and serves it via this same endpoint PDFViewer
  // uses to render the inline preview; this just triggers a file download
  // instead of rendering it.
  const handleDownloadOriginal = async () => {
    if (!sessionState.uploadResult?.documentId) {
      setError('No document uploaded. Please upload a PDF first.');
      return;
    }

    try {
      const downloadHeaders: Record<string, string> = {};
      const downloadHash = getStoredLicenseKeyHash();
      if (downloadHash) {
        downloadHeaders['X-License-Key-Hash'] = downloadHash;
      }

      const response = await fetch(
        getApiUrl(`/api/pdf/${sessionState.uploadResult.documentId}/file`),
        { credentials: 'include', headers: downloadHeaders }
      );

      if (!response.ok) {
        throw new Error(`Failed to download original PDF: ${response.status}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = sessionState.uploadResult.originalFilename || 'original.pdf';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading original PDF:', error);
      setError(`Failed to download original PDF: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  // PDF Generation handlers with correct API endpoints
  const handleGenerateFillablePDF = async (formData: any) => {
    console.log('Generate fillable PDF:', formData);
    
    if (!sessionState.uploadResult?.documentId) {
      setError('No document uploaded. Please upload a PDF first.');
      return;
    }

    // Edits apply live with no Apply step, so gate generation on validity here —
    // an invalid field (e.g. missing name) must not reach codegen.
    const invalidForGeneration = selectedFields.filter(f => getFieldValidationErrors(f).length > 0);
    if (invalidForGeneration.length > 0) {
      setError(
        `Cannot generate: ${invalidForGeneration.length} field(s) have invalid configuration ` +
        `(${invalidForGeneration.map(f => f.name || f.id).join(', ')}). ` +
        `Fix them in the field properties panel first.`
      );
      return;
    }

    // Convert frontend field data to backend FormConfiguration format
    const formConfiguration = {
      formId: sessionState.uploadResult.documentId,
      formName: projectName || sessionState.uploadResult.originalFilename || 'Generated Form',
      description: 'Form generated from PDF upload',
      // Per-form accessibility properties. documentTitle is the spoken/window
      // title, documentLanguage the BCP-47 tag behind /Lang and <html lang>.
      // formName stays what it always was: the project identifier.
      //
      // NO FALLBACK, deliberately. There used to be "|| projectName" here, which
      // put the filename back immediately after deriveDocumentTitle had chosen to
      // leave the box empty for a scanner filename - and after an author had
      // cleared it on purpose. An empty title means nobody has written one yet;
      // the backend then leaves /Title, dc:title and DisplayDocTitle unwritten and
      // the inline warning under the input asks for one. D43.
      documentTitle: documentTitle.trim(),
      documentLanguage: documentLanguage || DEFAULT_DOCUMENT_LANGUAGE,
      sourceDocumentId: sessionState.uploadResult.documentId,
      originalFilename: sessionState.uploadResult.originalFilename,
      // D37. AUTO lets the generator sort page-major by geometry; MANUAL means the
      // author arranged the field list themselves and the array below IS the reading
      // order. Geometry cannot tell you whether a two-column form reads across or
      // down, so the author has to be able to say.
      //
      // THE AUTHOR'S DECISION, NOT THE AUTHOR'S VIEW. This used to read
      // readingOrderModeFor(fieldSortPrefs), which made any sidebar sort other than
      // reading-order send MANUAL and published the sorted order as the PDF's
      // reading and tab order - so sorting by name to FIND a field shipped a form
      // that tabbed alphabetically (SC 2.4.3, Level A). fieldSortPrefs must not
      // appear anywhere in this payload.
      readingOrderMode: readingOrderMode,
      // The field array in the order the document will use it. orderForGeneration
      // takes the MODE: AUTO sorts page-major here as well as in the backend,
      // because the generated project bakes this array and carries no comparator
      // of its own (D51), so the two artefacts have to agree before they are built.
      fields: orderForGeneration(selectedFields, readingOrderMode).map((field) => ({
        fieldId: field.id,
        name: field.name,
        label: field.label || field.name,   // preserve the authored accessible name; fall back only for legacy fields
        type: field.type?.toUpperCase() || 'TEXT',
        fontSize: field.fontSize,
        textAlign: field.textAlign,
        coordinates: {
          x: field.x,
          y: field.y,
          width: field.width,
          height: field.height,
          pageNumber: field.pageNumber || 1
        },
        // The optional data-binding key, persisted so it survives reload and reaches the
        // generated-project emitter. Null/blank = an independent field (design §10).
        dataKey: field.dataKey,
        // Value resolution lives in ONE place — resolveFieldValue (fieldDataBinding.ts), shared
        // with the Live Preview so the preview and the deliverable cannot drift. The rule: a
        // non-empty TRIMMED entry wins; a blank/whitespace-only entry falls back to the Default
        // Value (the default is a SEED, never an override). Keyed by dataKey when set (shared
        // value across fields), else the field's own id. This SUPERSEDES the 2026-08-18
        // "cleared-stays-blank" behavior per the owner's trim-then-default model (design §4).
        defaultValue: resolveFieldValue(field, formData),
        formatHint: field.formatHint || '',
        required: field.required || false,
        // Was hard-coded `false` on BOTH generation paths, so every generated PDF
        // discarded the author's choice and emitted an editable field (QA, 22 Sep).
        // The checkbox, the persistence and the emitter were all correct; these two
        // lines threw the value away between them - which is why nothing caught it:
        // the backend tests set the model directly and never came through here.
        readOnly: field.readOnly || false,
        characterBlockConfig: field.characterBlockConfig,
        selectConfig: field.selectConfig,
        radioGroupConfig: field.radioGroupConfig,
        // An image region's two inputs. Nothing else on this field carries them: the text is
        // not a label (it either describes the picture or replaces it) and the purpose is not
        // a type. Omitted here, the backend would refuse the generation — which is the right
        // failure and the wrong place for it.
        imageRegionConfig: field.imageRegionConfig,
        // A paragraph region's role and text, carried the same way: omitted, the backend
        // would refuse to generate, which is the right failure in the wrong place.
        paragraphConfig: field.paragraphConfig,
        // Map selectConfig options to the backend's expected format
        options: field.selectConfig?.options?.map((opt: any) => ({
          value: opt.value || opt.label || '',
          label: opt.label || opt.value || '',
          selected: opt.selected || false
        })) || (field.radioGroupConfig?.options?.map((opt: any) => ({
          value: opt.value || opt.label || '',
          label: opt.label || opt.value || '',
          selected: opt.selected || false
        })) || undefined)
      })),
      createdAt: new Date().toISOString(),
      version: 1,
      status: 'DRAFT'
    };

    console.log('Sending FormConfiguration:', formConfiguration);

    try {
      const generateHeaders: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      const generateHash = getStoredLicenseKeyHash();
      if (generateHash) {
        generateHeaders['X-License-Key-Hash'] = generateHash;
      }

      const response = await fetch(
        getApiUrl(`/api/pdf/forms/${sessionState.uploadResult.documentId}/generate-fillable-pdf`),
        {
          method: 'POST',
          credentials: 'include',
          headers: generateHeaders,
          body: JSON.stringify(formConfiguration),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to generate fillable PDF: ${response.status}`);
      }

      // Handle PDF file download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${projectName || formData.formName || 'form'}_fillable.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      console.log('Fillable PDF generated and downloaded successfully!');
      setError(null);
    } catch (error) {
      console.error('Error generating fillable PDF:', error);
      setError(`Failed to generate fillable PDF: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  // The free look at the flattened result (design section 3.3.1). Held here
  // rather than in LiveFormPreview because this is where the request body is
  // built and where the real download lives - the preview and the deliverable
  // must come from the same configuration.
  // Section 3.5.6: surface a refused save with a way forward.
  const { showLimit, showGate } = useArtifactGate();
  const { capabilities } = useCapabilities();

  const [flattenedPreviewOpen, setFlattenedPreviewOpen] = useState(false);
  const [flattenedPreviewFormData, setFlattenedPreviewFormData] = useState<any>({});

  const handlePreviewFlattenedPDF = (formData: any) => {
    setFlattenedPreviewFormData(formData);
    setFlattenedPreviewOpen(true);
  };

  /**
   * The FormConfiguration the backend expects, built from what is on screen.
   *
   * Shared by the flattened DOWNLOAD and the flattened PREVIEW so the picture a
   * visitor is shown cannot drift from the document they receive. A preview
   * that renders different content from the deliverable is worse than no
   * preview, because it is believed.
   */
  const buildFormConfiguration = (formData: any) => {
    if (!sessionState.uploadResult?.documentId) {
      return null;
    }
    return {
      formId: sessionState.uploadResult.documentId,
      formName: projectName || sessionState.uploadResult.originalFilename || 'Generated Form',
      description: 'Form generated from PDF upload',
      // Per-form accessibility properties. documentTitle is the spoken/window
      // title, documentLanguage the BCP-47 tag behind /Lang and <html lang>.
      // formName stays what it always was: the project identifier.
      //
      // NO FALLBACK, deliberately. There used to be "|| projectName" here, which
      // put the filename back immediately after deriveDocumentTitle had chosen to
      // leave the box empty for a scanner filename - and after an author had
      // cleared it on purpose. An empty title means nobody has written one yet;
      // the backend then leaves /Title, dc:title and DisplayDocTitle unwritten and
      // the inline warning under the input asks for one. D43.
      documentTitle: documentTitle.trim(),
      documentLanguage: documentLanguage || DEFAULT_DOCUMENT_LANGUAGE,
      sourceDocumentId: sessionState.uploadResult.documentId,
      originalFilename: sessionState.uploadResult.originalFilename,
      // D37. AUTO lets the generator sort page-major by geometry; MANUAL means the
      // author arranged the field list themselves and the array below IS the reading
      // order. Geometry cannot tell you whether a two-column form reads across or
      // down, so the author has to be able to say.
      //
      // THE AUTHOR'S DECISION, NOT THE AUTHOR'S VIEW. This used to read
      // readingOrderModeFor(fieldSortPrefs), which made any sidebar sort other than
      // reading-order send MANUAL and published the sorted order as the PDF's
      // reading and tab order - so sorting by name to FIND a field shipped a form
      // that tabbed alphabetically (SC 2.4.3, Level A). fieldSortPrefs must not
      // appear anywhere in this payload.
      readingOrderMode: readingOrderMode,
      // The field array in the order the document will use it. orderForGeneration
      // takes the MODE: AUTO sorts page-major here as well as in the backend,
      // because the generated project bakes this array and carries no comparator
      // of its own (D51), so the two artefacts have to agree before they are built.
      fields: orderForGeneration(selectedFields, readingOrderMode).map((field) => ({
        fieldId: field.id,
        name: field.name,
        label: field.label || field.name,   // preserve the authored accessible name; fall back only for legacy fields
        type: field.type?.toUpperCase() || 'TEXT',
        fontSize: field.fontSize,
        textAlign: field.textAlign,
        coordinates: {
          x: field.x,
          y: field.y,
          width: field.width,
          height: field.height,
          pageNumber: field.pageNumber || 1
        },
        // The optional data-binding key, persisted so it survives reload and reaches the
        // generated-project emitter. Null/blank = an independent field (design §10).
        dataKey: field.dataKey,
        // Value resolution lives in ONE place — resolveFieldValue (fieldDataBinding.ts), shared
        // with the Live Preview so the preview and the deliverable cannot drift. The rule: a
        // non-empty TRIMMED entry wins; a blank/whitespace-only entry falls back to the Default
        // Value (the default is a SEED, never an override). Keyed by dataKey when set (shared
        // value across fields), else the field's own id. This SUPERSEDES the 2026-08-18
        // "cleared-stays-blank" behavior per the owner's trim-then-default model (design §4).
        defaultValue: resolveFieldValue(field, formData),
        formatHint: field.formatHint || '',
        required: field.required || false,
        // Was hard-coded `false` on BOTH generation paths, so every generated PDF
        // discarded the author's choice and emitted an editable field (QA, 22 Sep).
        // The checkbox, the persistence and the emitter were all correct; these two
        // lines threw the value away between them - which is why nothing caught it:
        // the backend tests set the model directly and never came through here.
        readOnly: field.readOnly || false,
        characterBlockConfig: field.characterBlockConfig,
        radioGroupConfig: field.radioGroupConfig,
        selectConfig: field.selectConfig,
        imageRegionConfig: field.imageRegionConfig,
        // A paragraph region's role and text, carried the same way: omitted, the backend
        // would refuse to generate, which is the right failure in the wrong place.
        paragraphConfig: field.paragraphConfig,
        options: field.selectConfig?.options?.map((opt: any) => ({
          value: opt.value || opt.label || '',
          label: opt.label || opt.value || '',
          selected: opt.selected || false
        })) || (field.radioGroupConfig?.options?.map((opt: any) => ({
          value: opt.value || opt.label || '',
          label: opt.label || opt.value || '',
          selected: opt.selected || false
        })) || undefined)
      })),
      createdAt: new Date().toISOString(),
      version: 1,
      status: 'DRAFT'
    };
  };


  const handleGenerateFlattenedPDF = async (formData: any) => {
    console.log('Generate flattened PDF:', formData);
    
    if (!sessionState.uploadResult?.documentId) {
      setError('No document uploaded. Please upload a PDF first.');
      return;
    }

    // Edits apply live with no Apply step, so gate generation on validity here —
    // an invalid field (e.g. missing name) must not reach codegen.
    const invalidForGeneration = selectedFields.filter(f => getFieldValidationErrors(f).length > 0);
    if (invalidForGeneration.length > 0) {
      setError(
        `Cannot generate: ${invalidForGeneration.length} field(s) have invalid configuration ` +
        `(${invalidForGeneration.map(f => f.name || f.id).join(', ')}). ` +
        `Fix them in the field properties panel first.`
      );
      return;
    }

    // Convert frontend field data to backend FormConfiguration format
    const formConfiguration = buildFormConfiguration(formData);
    if (!formConfiguration) {
      setError('No document uploaded. Please upload a PDF first.');
      return;
    }

    console.log('Sending FormConfiguration:', formConfiguration);
    console.log('Debug - Field default values for flattened PDF:', selectedFields.map(field => ({ 
      name: field.name, 
      configuredDefault: field.defaultValue,
      formValue: formData[field.id],
      finalDefault: formData[field.id] || field.defaultValue || ''
    })));

    try {
      const flattenHeaders: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      const flattenHash = getStoredLicenseKeyHash();
      if (flattenHash) {
        flattenHeaders['X-License-Key-Hash'] = flattenHash;
      }

      const response = await fetch(
        getApiUrl(`/api/pdf/forms/${sessionState.uploadResult.documentId}/generate-flattened-pdf`),
        {
          method: 'POST',
          credentials: 'include',
          headers: flattenHeaders,
          body: JSON.stringify(formConfiguration),
        }
      );

      if (!response.ok) {
        // The backend answers this endpoint with a binary body, so failures arrive in
        // headers. Read them before giving up: a tier denial carries the whole story
        // (tier, limit, count, reset date, who to contact) and a bare status number
        // told the customer none of it.
        const failure = describeDownloadFailure(
          response.status,
          response.headers,
          'FLATTENED_PDF_ERROR'
        );

        setErrorDialog({
          isOpen: true,
          title: 'Flattened PDF Generation Failed',
          message: failure.message,
          details: `Status: ${response.status}`,
          errorCode: failure.errorCode,
          timestamp: new Date().toISOString()
        });
        return;
      }

      // Handle PDF file download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${projectName || formData.formName || 'form'}_flattened.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      console.log('Flattened PDF generated and downloaded successfully!');
      // This consumed quota. LicenseContext listens for `license-changed`
      // and re-fetches; without it the counts stay stale until the 5-minute
      // poll. Same pattern applyLicenseKey and clearLicense already use.
      window.dispatchEvent(new Event('license-changed'));
      setError(null);
    } catch (error) {
      console.error('Error generating flattened PDF:', error);
      setError(`Failed to generate flattened PDF: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  /**
   * Opens the Generate Project dialog after pre-validation checks.
   * The actual generation happens in handleGenerateWithAllocation after the user
   * configures the allocation and confirms.
   */
  const handleGenerateSpringBootProject = async (formData: any) => {
    if (!sessionState.uploadResult?.documentId) {
      console.error('No document ID available for code generation');
      setError('No document uploaded');
      return;
    }

    if (!formData || formData.length === 0) {
      console.error('No form data provided for Spring Boot project generation');
      setError('Please select at least one field before generating the Spring Boot project');
      return;
    }

    // Check if session is saved
    if (!sessionState.currentSessionId) {
      setErrorDialog({
        isOpen: true,
        title: 'Session Not Saved',
        message: 'Please save the session before generating a Spring Boot project',
        details: 'A project is built from a saved session. Click Save session at the right-hand end of the toolbar above the PDF to save this work first, then generate.',
        errorCode: 'NO_SESSION_ID',
        timestamp: new Date().toISOString()
      });
      return;
    }

    // Check for unsaved changes
    if (sessionState.hasUnsavedChanges) {
      setErrorDialog({
        isOpen: true,
        title: 'Unsaved Changes',
        message: 'Please save your changes before generating a Spring Boot project',
        details: 'The session has field changes that are not saved yet — the Save Session / Update Session button is orange. Save it, then generate: a project is always built from a saved session revision.',
        errorCode: 'UNSAVED_CHANGES',
        timestamp: new Date().toISOString()
      });
      return;
    }

    // Pre-validation passed — open the allocation dialog
    setPendingGenerationFields(formData);
    setShowGenerateDialog(true);
  };

  /**
   * Called from GenerateProjectDialog after user configures allocation and confirms.
   * Performs the actual generation with allocation data included.
   */
  const handleGenerateWithAllocation = async (config: ProjectGenerationConfig) => {
    if (!sessionState.uploadResult?.documentId || !pendingGenerationFields) return;

    setIsGeneratingProject(true);

    try {
      console.log('Generating Spring Boot project with allocation:', config);

      const formRequest = {
        sessionId: sessionState.currentSessionId,
        formName: config.projectName,
        // Same two properties on the project path. The generated web form needs
        // them too: <html lang> is currently the literal "en" and <title> a
        // "%s - Form Test" template. This endpoint parses its body as a Map, so
        // the keys are carried whether or not the generator reads them yet.
        // No "|| config.projectName" fallback - see the note on the two
        // formConfiguration builders above. D43.
        documentTitle: documentTitle.trim(),
        documentLanguage: documentLanguage || DEFAULT_DOCUMENT_LANGUAGE,
        flattenedPdfAllocation: config.flattenedPdfAllocation,
        comments: config.comments,
      };

      const genHeaders: Record<string, string> = { 'Content-Type': 'application/json' };
      const genHash = getStoredLicenseKeyHash();
      if (genHash) {
        genHeaders['X-License-Key-Hash'] = genHash;
      }

      const response = await fetch(
        getApiUrl(`/api/pdf/forms/${sessionState.uploadResult.documentId}/generate-code`),
        {
          method: 'POST',
          credentials: 'include',
          headers: genHeaders,
          body: JSON.stringify(formRequest),
        }
      );

      if (!response.ok) {
        const errorHeader = response.headers.get('X-Error-Message');
        let errorMessage = 'Failed to generate Spring Boot project';

        if (errorHeader) {
          errorMessage = errorHeader;
        } else {
          try {
            const errorText = await response.text();
            if (errorText) {
              errorMessage = errorText;
            }
          } catch (e) {
            errorMessage = `Failed to generate Spring Boot project: ${response.status}`;
          }
        }

        setErrorDialog({
          isOpen: true,
          title: 'Spring Boot Project Generation Failed',
          message: errorMessage,
          details: `Status: ${response.status}`,
          errorCode: 'SPRING_BOOT_GENERATION_ERROR',
          timestamp: new Date().toISOString()
        });
        return;
      }

      // Handle ZIP file download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${config.projectName}_spring_boot_project.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      console.log(`Spring Boot project "${config.projectName}" generated and downloaded successfully!`);
      // This consumed quota. LicenseContext listens for `license-changed`
      // and re-fetches; without it the counts stay stale until the 5-minute
      // poll. Same pattern applyLicenseKey and clearLicense already use.
      window.dispatchEvent(new Event('license-changed'));

      // Advance the Getting Started guide to its final stage
      window.dispatchEvent(new CustomEvent('pfb-project-generated'));

      // Close dialog on success
      setShowGenerateDialog(false);
      setPendingGenerationFields(null);

    } catch (error: any) {
      console.error('Error generating Spring Boot project:', error);
      setErrorDialog({
        isOpen: true,
        title: 'Spring Boot Project Generation Failed',
        message: error.message || 'Unknown error occurred while generating Spring Boot project',
        details: error.stack || error.toString(),
        errorCode: 'SPRING_BOOT_GENERATION_ERROR',
        timestamp: new Date().toISOString()
      });
    } finally {
      setIsGeneratingProject(false);
    }
  };

  const handleFormSubmit = async (formData: any) => {
    console.log('Form submitted with data:', formData);
    alert('Form submitted successfully! (This is a demo - data logged to console)');
  };

  return (
    <>
      <div className="w-full">
        {/* Progress Indicator and License Status */}
        <div className="max-w-6xl mx-auto mb-6">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Progress Indicator */}
            <div className="flex-grow bg-white/80 backdrop-blur-sm rounded-xl shadow-soft-sm p-4 border border-neutral-200">
              <ProgressIndicator
                steps={workflowSteps}
                currentStep={currentWorkflowStep}
                orientation="horizontal"
                showDescription={true}
                compact={false}
              />
            </div>
            {/* License Status Widget */}
            <div className="lg:w-72 flex-shrink-0">
              <LicenseStatus className="h-full" />
            </div>
          </div>
        </div>

        {/* The way in. Gated on capabilities having RESOLVED, never on which
            tier resolved.

            This asked `licenseInfo.tier !== 'NO_LICENSE'` until 2026-09-04,
            which hid the entry point from exactly the visitors anonymous mode
            exists for: WorkflowChoice is what sets workflowPath to 'new', and
            only that renders UploadSection - so a guest saw a header, a licence
            widget, and nothing to click. Phase 3 inverted AuthGatedWorkflow but
            this second gate sat a level deeper.

            Not gated on canUploadPdf either. This panel also leads to saved
            sessions, so a guest who has spent their uploads must still reach
            their work; the upload attempt itself is bounded server-side and
            answers with a factual message and a way forward (section 3.3.2).
            Hiding the door is the dead end that rule exists to prevent. */}
        {workflowPath === 'none' && capabilities && (
          <WorkflowChoice
            onSelectWorkflow={handleSelectWorkflow}
            onLoadSessions={fetchAvailableSessions}
          />
        )}

        {/* Upload Section */}
        {workflowPath === 'new' && (
          <>
            <UploadSection
              selectedFile={selectedFile}
              uploadResult={sessionState.uploadResult}
              isUploading={isUploading}
              error={error}
              selectedFields={selectedFields}
              onFileSelect={handleFileSelect}
              onUpload={handleUpload}
              onBack={handleBackToWorkflowChoice}
              onDownloadOriginal={handleDownloadOriginal}
            />
            
          </>
        )}

        {/* Form Details — Project Name, Document Title, Language. Shown whenever a
            document is being authored: a fresh upload (selectedFile) OR a loaded
            session (documentId). It previously sat INSIDE the workflowPath==='new'
            fragment, so a LOADED session showed no Title/Language fields and a resave
            blank-wiped the stored values (docs/design/DOC_TIER_PERSISTENCE_BUG_2026-10-26.md).

            Project Name Section - shown after file selection.

                COMPACT ON PURPOSE (owner, 6 Oct: the card was "very tall for
                what it is and can take up an entire screen"). Every word
                survives — the helper texts and the orange warnings are
                accessibility education the owner values, and a hover-only
                tooltip would hide them from keyboard and touch users — but
                the CHROME is tightened: p-4 not p-6, py-2 inputs not py-3,
                and the title and language share a row at sm+ because they
                are two short answers, not two paragraphs. */}
            {(selectedFile || sessionState.uploadResult.documentId) && (
              <div className="max-w-2xl mx-auto mb-6">
                <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-soft-sm p-4 border border-neutral-200">
                  <div className="flex items-center mb-2">
                    <svg className="w-5 h-5 text-primary-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                    </svg>
                    <h3 className="text-lg font-display font-semibold text-neutral-900">Form Details</h3>
                  </div>
                  <p className="text-sm text-neutral-600 mb-3">
                    Three things about the form as a whole. The project name drives the generated code;
                    the title and language are what someone using a screen reader hears.
                  </p>
                  <div className="space-y-2">
                    <label htmlFor="projectName" className="block text-sm font-medium text-neutral-700">
                      Project Name
                    </label>
                    <input
                      type="text"
                      id="projectName"
                      value={projectName}
                      onChange={(e) => setProjectName(e.target.value.slice(0, MAX_PROJECT_NAME_LENGTH))}
                      maxLength={MAX_PROJECT_NAME_LENGTH}
                      placeholder="Enter project name..."
                      className="w-full px-4 py-2 border border-neutral-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors bg-white text-neutral-900 placeholder-neutral-500"
                    />
                    {selectedFile && (
                      <div className="flex items-center justify-between text-xs text-neutral-500">
                        <span>Derived from: {sanitizeFilename(selectedFile.name)} · max {MAX_PROJECT_NAME_LENGTH} chars ({projectName.length}/{MAX_PROJECT_NAME_LENGTH})</span>
                        <button
                          onClick={() => setProjectName(deriveProjectName(sanitizeFilename(selectedFile.name)))}
                          className="text-primary-600 hover:text-primary-700 font-medium"
                        >
                          Reset to derived name
                        </button>
                      </div>
                    )}
                  </div>

                  {/* ------------------------------------------------------------
                      Per-form accessibility properties.

                      These are the two document-level things only a human can
                      supply. Everything else a conformant file needs at document
                      level (marked flag, structure tree root, parent tree) the
                      generator writes with no input from anyone.
                      ------------------------------------------------------------ */}
                  {/* Side by side at sm+ — two short answers share a row; they
                      stack below it so neither column ever narrows past use. */}
                  <div className="mt-3 pt-3 border-t border-neutral-200 grid gap-x-4 gap-y-3 sm:grid-cols-2">
                    <div className="space-y-1.5">
                      <label htmlFor="documentTitle" className="block text-sm font-medium text-neutral-700">
                        Document Title
                      </label>
                      <input
                        type="text"
                        id="documentTitle"
                        value={documentTitle}
                        onChange={(e) => {
                          // The author has taken the title over — stop auto-seeding it
                          // (see documentTitleTouchedRef).
                          documentTitleTouchedRef.current = true;
                          setDocumentTitle(e.target.value);
                        }}
                        maxLength={500}
                        title={GUIDANCE_DOCUMENT_TITLE}
                        aria-describedby="documentTitleHelp"
                        placeholder="e.g. Dog Licence Application"
                        className="w-full px-4 py-2 border border-neutral-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors bg-white text-neutral-900 placeholder-neutral-500"
                      />
                      <p id="documentTitleHelp" className="text-xs text-neutral-600">
                        {GUIDANCE_DOCUMENT_TITLE}
                      </p>
                      {!documentTitle.trim() && (
                        <p className="text-xs text-orange-600">
                          This form has no title. Someone opening it hears the filename, which is
                          usually meaningless — type the name of the form as you would say it.
                        </p>
                      )}
                    </div>

                    <div className="space-y-1.5">
                      <label htmlFor="documentLanguage" className="block text-sm font-medium text-neutral-700">
                        Language
                      </label>
                      <select
                        id="documentLanguage"
                        value={useCustomLanguage ? 'other' : documentLanguage}
                        onChange={(e) => {
                          // The author has taken the language over — stop auto-seeding it
                          // (see documentLanguageTouchedRef).
                          documentLanguageTouchedRef.current = true;
                          if (e.target.value === 'other') {
                            setUseCustomLanguage(true);
                          } else {
                            setUseCustomLanguage(false);
                            setDocumentLanguage(e.target.value);
                          }
                        }}
                        title={GUIDANCE_DOCUMENT_LANGUAGE}
                        aria-describedby="documentLanguageHelp"
                        className="w-full px-4 py-2 border border-neutral-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors bg-white text-neutral-900"
                      >
                        {/* DOC-03. A session saved before document_language was persisted
                            restores with NO language, and the restore no longer invents en-US
                            for it. Without an option for the empty value the browser would
                            display the first entry as though it were chosen — the same
                            fabrication one control over. This makes "not chosen" visible, and
                            the warning below then asks for an answer. */}
                        <option value="">— Choose a language —</option>
                        {DOCUMENT_LANGUAGE_OPTIONS.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                        <option value="other">Other…</option>
                      </select>
                      {useCustomLanguage && (
                        <input
                          type="text"
                          value={documentLanguage}
                          onChange={(e) => {
                            documentLanguageTouchedRef.current = true;
                            setDocumentLanguage(e.target.value.trim());
                          }}
                          maxLength={100}
                          placeholder="Language tag, e.g. fr-CA"
                          aria-label="Custom language tag"
                          className="w-full px-4 py-2 border border-neutral-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors bg-white text-neutral-900 placeholder-neutral-500"
                        />
                      )}
                      <p id="documentLanguageHelp" className="text-xs text-neutral-600">
                        {GUIDANCE_DOCUMENT_LANGUAGE}
                      </p>
                      {!documentLanguage.trim() && (
                        <p className="text-xs text-orange-600">
                          Choose a language. A form with none is one of the few things a checker
                          fails outright.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

        {/* Main Content Area - PDF Viewer and Live Form Preview */}
        {sessionState.uploadResult.documentId && (
          <div className="w-full">
            {/* Generation Controls - Show when fields exist */}
            {selectedFields.length > 0 && (
              <div className="max-w-7xl mx-auto mb-8">
                <GenerationControls
                  fields={selectedFields}
                  onGenerateFillablePDF={handleGenerateFillablePDF}
                  onGenerateFlattenedPDF={handleGenerateFlattenedPDF}
                  onGenerateSpringBootProject={handleGenerateSpringBootProject}
                  onOpenLivePreview={() => setCurrentView('form')}
                />
              </div>
            )}

            {/* View Toggle Buttons with New Project navigation */}
            <div className="max-w-7xl mx-auto mb-6 view-toggle-buttons">
              <div className="flex justify-center items-center gap-4">
                <button
                  onClick={handleBackToWorkflowChoice}
                  className="flex items-center px-4 py-2.5 rounded-xl text-sm font-semibold text-neutral-600 hover:text-neutral-900 hover:bg-white/80 border border-neutral-200 hover:border-neutral-300 transition-all duration-200 shadow-sm"
                  title="Start a new project or load a different session"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" />
                  </svg>
                  Home
                </button>
                <div className="bg-white/80 backdrop-blur-md rounded-2xl shadow-soft-md p-1.5 border border-neutral-200">
                  <button
                    onClick={() => setCurrentView('pdf')}
                    className={`px-6 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 ${
                      currentView === 'pdf'
                        ? 'bg-gradient-to-r from-primary-600 to-primary-700 text-white shadow-soft-md transform scale-105'
                        : 'text-neutral-700 hover:text-neutral-900 hover:bg-neutral-100'
                    }`}
                  >
                    <div className="flex items-center">
                      <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                      </svg>
                      PDF Form Builder
                    </div>
                  </button>
                  <button
                    onClick={() => setCurrentView('form')}
                    className={`px-6 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 ${
                      currentView === 'form'
                        ? 'bg-gradient-to-r from-primary-600 to-primary-700 text-white shadow-soft-md transform scale-105'
                        : 'text-neutral-700 hover:text-neutral-900 hover:bg-neutral-100'
                    }`}
                  >
                    <div className="flex items-center">
                      <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z" />
                      </svg>
                      Live Form Preview
                    </div>
                  </button>
                </div>
              </div>
            </div>

            {/* Content Layout Based on Current View.
                THE WIDTH CAP IS DELIBERATELY CONDITIONAL, AND THE EDITOR IS
                DELIBERATELY WIDER THAN THE CHROME ABOVE IT. Scott, 18 Sep: "The PDF
                Viewer container does not need to be the same width of the other
                windows higher in the page, this is a real working area which needs
                breathing room."

                Everything above this - the progress row, GenerationControls, the view
                toggle - is capped at max-w-7xl (1280px) and centred. This wrapper is
                not: in PDF view it runs the full window less App's own 32px section
                gutter, so past ~1344px the editor is strictly wider than the chrome
                above it, and at 2560px it is nearly twice as wide.

                THE CAP WAS ALSO ONE LEVEL UP, and that is what actually bound. A
                max-w-7xl here was the first defect and xl:col-span-2 could not help,
                because the cap applies to this wrapper before the grid spreads - but
                removing it changed nothing measurable while App.tsx still wrapped the
                whole application in max-w-7xl. `w-full` inside a 1280px box is 1280px.
                Both had to go; see the note in App.tsx.

                The form preview KEEPS the cap: it is prose and controls, where an
                unbounded line length is genuinely harder to read (~75 characters is the
                ceiling). The PDF viewer is a canvas and should take the window. */}
            <div className={currentView === 'pdf' ? 'w-full' : 'max-w-7xl mx-auto'}>
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
                {/* PDF Viewer */}
                <div className={`pdf-viewer-section bg-white rounded-2xl shadow-soft-lg border border-neutral-200 overflow-hidden transition-all duration-300 hover:shadow-soft-xl ${
                  currentView === 'form' ? 'hidden xl:hidden' : ''
                } ${currentView === 'pdf' ? 'xl:col-span-2' : ''}`}>
                  <PDFViewer
                    documentId={sessionState.uploadResult.documentId}
                    originalFilename={sessionState.uploadResult.originalFilename}
                    projectName={projectName}
                    onFieldSelect={handleFieldSelect}
                    selectedFields={selectedFields}
                    onFieldUpdate={handleFieldUpdate}
                    onFieldsReorder={handleFieldsReorder}
                    onFieldDelete={handleFieldDelete}
                    hasUnsavedChanges={hasUnsavedChanges}
                    currentSessionId={currentSessionId}
                    isAutoCreatedSession={isAutoCreatedSession}
                    onUnsavedChangesUpdate={(hasChanges) => {
                      console.log('PDFUpload: onUnsavedChangesUpdate called from PDFViewer', { hasChanges, current: hasUnsavedChanges });
                      if (!hasChanges) {
                        // Session was just persisted to the server — refresh the snapshot
                        // so subsequent dirty-checks compare against what was actually saved.
                        // (SET_SELECTED_FIELDS won't update the snapshot when payload === current.)
                        dispatch(sessionActions.saveSucceeded());
                        // The save wrote the current Title/Language too (they are in the
                        // save payload), so they become the new clean baseline.
                        snapshotDocTier(documentTitle, documentLanguage);
                      } else {
                        dispatch(sessionActions.setUnsavedChanges(hasChanges));
                      }
                    }}
                    onSessionSaved={(sessionId, sessionName) => {
                      // Only set context sessionId for code generation — don't set local
                      // currentSessionId here, as that's only set when loading a session.
                      // This ensures new saves create new entries (save points) while
                      // loaded sessions continue their chain of progress.
                      dispatch(sessionActions.setCurrentSession(sessionId, sessionName));
                    }}
                    onLoadSession={loadSessionById}
                    fieldSortPrefs={fieldSortPrefs}
                    onFieldSortPrefsChange={handleFieldSortPrefsChange}
                    readingOrderMode={readingOrderMode}
                    onReadingOrderModeChange={setReadingOrderMode}
                    documentTitle={documentTitle}
                    documentLanguage={documentLanguage}
                  />
                </div>

                {/* Live Form Preview */}
                <div className={`form-preview-section bg-white rounded-2xl shadow-soft-lg border border-neutral-200 overflow-hidden transition-all duration-300 hover:shadow-soft-xl ${
                  currentView === 'pdf' ? 'hidden xl:hidden' : ''
                } ${currentView === 'form' ? 'xl:col-span-2' : ''}`}>
                  <div className="p-6 border-b border-neutral-200 bg-gradient-to-r from-primary-50/30 to-secondary-50/30">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-display font-semibold text-neutral-900">Live Form Preview</h3>
                        <p className="text-sm text-neutral-600 mt-1">Real-time preview of your interactive form</p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="inline-flex items-center px-3 py-1 text-xs font-medium text-success-700 bg-success-100 rounded-full">
                          <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                            <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                          </svg>
                          Live Preview
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="p-4 generate-buttons">
                    <LiveFormPreview
                      fields={selectedFields}
                      formName={`Generated Form - ${sanitizeFilename(sessionState.uploadResult.originalFilename || 'Document')}`}
                      onFieldChange={handleFormDataChange}
                      onGenerateFillablePDF={handleGenerateFillablePDF}
                      onGenerateFlattenedPDF={handleGenerateFlattenedPDF}
                  onPreviewFlattenedPDF={handlePreviewFlattenedPDF}
                      onGenerateSpringBootProject={handleGenerateSpringBootProject}
                      onSubmitForm={handleFormSubmit}
                      fieldSortPrefs={fieldSortPrefs}
                      onFieldSortPrefsChange={handleFieldSortPrefsChange}
                      readingOrderMode={readingOrderMode}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Load Session Dialog */}
      <LoadSessionDialog
        isOpen={showLoadDialog}
        isLoading={isLoadingSessions}
        sessions={availableSessions}
        onClose={handleCloseLoadDialog}
        onLoadSession={handleLoadSession}
        currentPdfFilename={sessionState.uploadResult.originalFilename || undefined}
      />

      {/* Generate Project Dialog */}
      <GenerateProjectDialog
        isOpen={showGenerateDialog}
        onClose={() => { setShowGenerateDialog(false); setPendingGenerationFields(null); }}
        onGenerate={handleGenerateWithAllocation}
        licenseInfo={licenseInfo}
        documentName={sessionState.uploadResult?.originalFilename ?? 'Unknown'}
        isGenerating={isGeneratingProject}
      />

      {/* Error Dialog */}
      <ErrorDialog
        isOpen={errorDialog.isOpen}
        title={errorDialog.title}
        message={errorDialog.message}
        details={errorDialog.details}
        errorCode={errorDialog.errorCode}
        timestamp={errorDialog.timestamp}
        onClose={handleErrorDialogClose}
        onRetry={handleErrorDialogRetry}
      />

      {/* The free look at the flattened result - design section 3.3.1. */}
      <FlattenedPreviewModal
        open={flattenedPreviewOpen}
        onClose={() => setFlattenedPreviewOpen(false)}
        documentId={sessionState.uploadResult?.documentId || ''}
        formData={flattenedPreviewFormData}
        buildFormConfiguration={buildFormConfiguration}
        licenseKeyHash={getStoredLicenseKeyHash()}
        onDownload={() => {
          setFlattenedPreviewOpen(false);
          handleGenerateFlattenedPDF(flattenedPreviewFormData);
        }}
      />

      {/* Unsaved Changes Confirmation Dialog */}
      {showUnsavedChangesDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            {/* Header */}
            <div className="px-6 py-4 border-b border-neutral-200 bg-gradient-to-r from-yellow-50 to-orange-50">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center mr-3">
                  <svg className="w-6 h-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <h2 className="text-xl font-display font-semibold text-neutral-900">Session Differences Detected</h2>
              </div>
            </div>

            {/* Content */}
            <div className="px-6 py-4">
              <p className="text-neutral-700 mb-4">
                The session you're trying to load has different field configurations than your current session. Loading it will replace your current work.
              </p>
              <div className="bg-neutral-50 rounded-lg p-3 mb-4">
                <p className="text-sm text-neutral-600 mb-3">
                  <strong>Summary:</strong> {sessionComparison?.summary}
                </p>
                
                {/* Quick counts overview */}
                {sessionComparison?.counts && (
                  <div className="flex gap-4 mb-3 text-xs">
                    {sessionComparison.counts.added > 0 && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full bg-green-100 text-green-700">
                        ➕ {sessionComparison.counts.added} added
                      </span>
                    )}
                    {sessionComparison.counts.removed > 0 && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full bg-red-100 text-red-700">
                        ➖ {sessionComparison.counts.removed} removed
                      </span>
                    )}
                    {sessionComparison.counts.modified > 0 && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full bg-blue-100 text-blue-700">
                        🔄 {sessionComparison.counts.modified} modified
                      </span>
                    )}
                  </div>
                )}
                
                {sessionComparison?.details && sessionComparison.details.length > 0 && (
                  <div>
                    <p className="text-xs text-neutral-500 mb-2 font-medium">Detailed changes:</p>
                    <div className="max-h-32 overflow-y-auto">
                      <ul className="text-xs text-neutral-500 space-y-1">
                        {sessionComparison.details.slice(0, 8).map((diff: string, index: number) => (
                          <li key={index} className="font-mono">• {diff}</li>
                        ))}
                        {sessionComparison.details.length > 8 && (
                          <li className="italic text-neutral-400">... and {sessionComparison.details.length - 8} more differences</li>
                        )}
                      </ul>
                    </div>
                  </div>
                )}
              </div>
              <p className="text-sm text-neutral-600">
                Consider saving your current session before loading the other session to preserve your work.
              </p>
            </div>

            {/* Actions */}
            <div className="px-6 py-4 border-t border-neutral-200 bg-neutral-50 flex justify-between space-x-3">
              <button
                onClick={handleCancelLoadSession}
                className="px-4 py-2.5 rounded-xl border border-neutral-300 text-neutral-700 hover:bg-neutral-100 transition-colors font-medium"
              >
                Cancel
              </button>
              <div className="flex space-x-2">
                <button
                  onClick={handleDiscardChanges}
                  className="px-4 py-2.5 rounded-xl border border-red-300 text-red-700 hover:bg-red-50 transition-colors font-medium"
                >
                  Load Anyway
                </button>
                <button
                  onClick={handleSaveAndContinue}
                  className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-emerald-600 to-emerald-700 text-white hover:from-emerald-700 hover:to-emerald-800 transition-all duration-200 font-medium"
                >
                  Save Current & Load
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PDFUpload;